import { ErrorAlert } from 'components/error-fallback'
import {
    GeneralizedComponent,
    GeneralizedComponentPayloadType,
} from 'components/generalized-component/generalized-component'
import { BasicPlaceholder } from 'components/placeholder'
import { useBasketSales } from 'hooks/sales/useBasketSales'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import React, { useMemo } from 'react'
import { TableData } from 'utils/data'
interface BasketHistoricalProps {
    timePeriodIndex: number
}

const BasketHistorical: React.FC<BasketHistoricalProps> = ({
    timePeriodIndex,
}) => {
    const { currentLocation, env, session, t, isEnglish } = useEssentials()
    const { overview } = TRANSLATION_KEYS.sales

    const placeholder = useMemo(
        () => <BasicPlaceholder type="table" rows={5} />,
        [],
    )

    const { data, isLoading, isError, error, refetch } = useBasketSales({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        timePeriodIndex,
    })

    const convertedPayload = useMemo<GeneralizedComponentPayloadType>(() => {
        if (!data) return {}

        // using footer to get "Total" data
        const footerData = data.footer ?? []

        const getFooterValue = (idx: number) => footerData[idx]?.value || ''

        const rowSpecs: Array<{
            label: string
            indices: [number, number, number]
        }> = [
            { label: t(overview.total), indices: [1, 5, 9] },
            { label: t(overview.ly), indices: [2, 6, 10] },
            { label: t(overview.bpToLy), indices: [4, 8, 12] },
            { label: t(overview.bpToLyPercent), indices: [3, 7, 11] },
        ]

        const table: TableData = {
            headings: [
                { value: ' ' }, // top-left blank (extra space intentionally kept)
                { value: t(overview.basketSizeDollar) },
                { value: t(overview.basketSizeUnits) },
                { value: t(overview.transactions) },
            ],
            rows: rowSpecs.map((rs) => [
                { value: rs.label },
                { value: getFooterValue(rs.indices[0]) },
                { value: getFooterValue(rs.indices[1]) },
                { value: getFooterValue(rs.indices[2]) },
            ]),
        }

        const payload: GeneralizedComponentPayloadType = {
            '': [
                {
                    title: '',
                    subtitle: '',
                    data: { default: table },
                },
            ],
        }

        return payload
    }, [data, overview, t])

    if (isError) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    return (
        <div className="sales-historical-basket">
            <GeneralizedComponent
                payload={convertedPayload}
                isLoading={isLoading}
                loadingComponent={placeholder}
                widths={isEnglish ? [75, 90, 90, 90] : [75, 93, 93, 93]}
            />
        </div>
    )
}

export default BasketHistorical
